import {useState, useEffect} from "react";
import moment from "moment";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Grid, Chip, CircularProgress,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import ListHead from "src/components/common/ListHead";
import {asyncRequest} from "src/services/network";

const TABLE_HEAD = [
  {id: "createdAt", label: "时间", alignRight: false},
  {id: "type", label: "类型", alignRight: false},
  {id: "detail", label: "来源", alignRight: false},
  {id: "amount", label: "变动金额", alignRight: true},
  {id: "balanceAfter", label: "池余额", alignRight: true},
  {id: "priceBefore", label: "涨前单价", alignRight: true},
  {id: "priceAfter", label: "涨后单价", alignRight: true},
  {id: "priceDelta", label: "涨跌", alignRight: true},
];

const TYPE_MAP = {
  PAYMENT: "入池",
  REDEEM: "兑现",
  ADJUST: "调整",
  INIT: "初始化",
  RECHARGE: "充值",
  OTHER: "其他",
};

const TYPE_COLOR = {
  PAYMENT: "success",
  REDEEM: "error",
  ADJUST: "warning",
  INIT: "info",
  RECHARGE: "primary",
  OTHER: "default",
};

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function fmtPrice(v) {
  if (v === null || v === undefined) return "-";
  return Number(v).toFixed(6);
}

function fmtSigned(v) {
  const n = Number(v) || 0;
  return (n >= 0 ? "+" : "") + fmtMoney(n);
}

function fmtSignedPrice(v) {
  if (v === null || v === undefined) return "-";
  const n = Number(v) || 0;
  return (n >= 0 ? "+" : "") + n.toFixed(6);
}

function SummaryCard({title, value, sub}) {
  return (
    <Card sx={{p: 3, height: "100%"}}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
      <Typography variant="h4" sx={{wordBreak: "break-all"}}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Card>
  );
}

export default function ReleaseOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const res = await asyncRequest({
        method: "GET",
        path: "/jingxi/release-overview",
      });
      setData(res.data || null);
    } catch (e) {
      console.error("获取释放总览失败:", e);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Page title="金银通释放总览">
        <Container maxWidth="2000px">
          <Stack sx={{width: "100%", height: 300}} alignItems="center" justifyContent="center">
            <CircularProgress/>
          </Stack>
        </Container>
      </Page>
    );
  }

  const equity = data?.equity || {};
  const fundPool = data?.fundPool || {};
  const events = data?.events || [];

  return (
    <Page title="金银通释放总览">
      <Container maxWidth="2000px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" gutterBottom>金银通释放总览</Typography>
        </Stack>

        {/* 全网权益值 */}
        <Typography variant="h6" sx={{mb: 2}}>全网权益值</Typography>
        <Grid container spacing={3} sx={{mb: 4}}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="当前权益值（剩余待释放）" value={`¥ ${fmtMoney(equity.curEquity)}`}
              sub={`持有 ${equity.hasEquityUsers} 人 / 金银通账户 ${equity.totalUsers} 个`}/>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="累积权益值" value={`¥ ${fmtMoney(equity.cumEquity)}`}
              sub="含已释放过的部分"/>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="冻结权益值" value={`¥ ${fmtMoney(equity.frozenEquity)}`}/>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard title="开启自动释放" value={`${equity.autoReleaseOn} 人`}
              sub="auto_release=1 且有权益值"/>
          </Grid>
        </Grid>

        {/* 拼团资金底池 */}
        <Typography variant="h6" sx={{mb: 2}}>拼团资金底池（id=3）</Typography>
        <Grid container spacing={3} sx={{mb: 4}}>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard title="底池金额 currentAmount" value={`¥ ${fmtMoney(fundPool.currentAmount)}`}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard title="贡献值单价 currentPrice" value={fmtPrice(fundPool.currentPrice)}/>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <SummaryCard title="最近更新时间" value={fundPool.lastPriceUpdateTime
              ? moment(fundPool.lastPriceUpdateTime).format("YYYY-MM-DD HH:mm:ss") : "-"}/>
          </Grid>
        </Grid>

        {/* 底池资金明细（每次涨价/入池） */}
        <Typography variant="h6" sx={{mb: 2}}>
          底池资金明细（每一次入池 / 涨价，共 {events.length} 条）
        </Typography>
        <Card>
          <Scrollbar>
            <TableContainer sx={{minWidth: 1400}}>
              <Table>
                <ListHead headLabel={TABLE_HEAD}/>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{py: 5}}>
                        <Typography color="text.secondary">暂无数据</Typography>
                      </TableCell>
                    </TableRow>
                  ) : events.map((row) => {
                    const up = (row.amount || 0) >= 0;
                    const priceUp = (row.priceDelta || 0) > 0;
                    const priceDown = (row.priceDelta || 0) < 0;
                    return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell align="left">{row.createdAt ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</TableCell>
                        <TableCell align="left">
                          <Chip size="small" label={TYPE_MAP[row.type] || row.type} color={TYPE_COLOR[row.type] || "default"}/>
                        </TableCell>
                        <TableCell align="left" sx={{maxWidth: 420}}>
                          <Typography variant="body2" noWrap title={row.detail}>{row.detail || "-"}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{color: up ? "success.main" : "error.main", fontWeight: 600}}>
                          {fmtSigned(row.amount)}
                        </TableCell>
                        <TableCell align="right">{fmtMoney(row.balanceAfter)}</TableCell>
                        <TableCell align="right">{fmtPrice(row.priceBefore)}</TableCell>
                        <TableCell align="right">{fmtPrice(row.priceAfter)}</TableCell>
                        <TableCell align="right" sx={{
                          color: priceUp ? "error.main" : priceDown ? "success.main" : "text.secondary",
                          fontWeight: 600,
                        }}>
                          {row.priceDelta === null ? "-" : fmtSignedPrice(row.priceDelta)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
